<template>
  <div
    class="ui-number-field"
    :class="[
      sizeClass,
      {
        'is-block': block,
        'is-disabled': disabled,
        'has-prefix': Boolean(prefix),
        'has-unit': Boolean(unit)
      }
    ]"
  >
    <span v-if="prefix" class="ui-number-field__addon ui-number-field__prefix">{{ prefix }}</span>
    <el-input-number
      ref="control"
      class="ui-number-input"
      :value="value"
      v-bind="$attrs"
      v-on="forwardedListeners"
      @input="$emit('input', $event)"
    />
    <span v-if="unit" class="ui-number-field__addon ui-number-field__unit">{{ $t(unit) }}</span>
  </div>
</template>

<script>
export default {
  name: 'UiNumberInput',
  inheritAttrs: false,
  props: {
    value: { default: 0 },
    block: { type: Boolean, default: false },
    prefix: { type: String, default: '' },
    unit: { type: String, default: '' }
  },
  computed: {
    forwardedListeners () {
      const listeners = { ...this.$listeners }
      delete listeners.input
      return listeners
    },
    disabled () {
      return this.$attrs.disabled !== undefined && this.$attrs.disabled !== false
    },
    sizeClass () {
      return this.$attrs.size ? `is-size-${this.$attrs.size}` : ''
    },
    nativeInputMode () {
      if (this.$attrs.inputmode) return this.$attrs.inputmode
      const step = this.$attrs.step === undefined ? 1 : Number(this.$attrs.step)
      return Number.isInteger(step) ? 'numeric' : 'decimal'
    }
  },
  mounted () {
    this.syncNativeInputMode()
  },
  updated () {
    this.syncNativeInputMode()
  },
  methods: {
    syncNativeInputMode () {
      const input = this.$el.querySelector('input')
      if (input) input.setAttribute('inputmode', this.nativeInputMode)
    },
    focus () {
      const input = this.$el.querySelector('input')
      if (input) input.focus()
    }
  }
}
</script>

<style lang="scss">
.ui-number-field {
  --ui-number-height: var(--ui-control-height);
  --ui-number-step-width: var(--ui-control-height-compact);
  --ui-number-input-width: 140px;

  max-width: 100%;
  height: var(--ui-number-height);
  display: inline-flex;
  align-items: stretch;
  overflow: hidden;
  color: var(--ui-text-primary);
  border: 1px solid var(--ui-control-rest-border);
  border-radius: var(--ui-radius-medium);
  background: linear-gradient(var(--ui-control-rest-background), var(--ui-control-rest-background)), var(--ui-bg-control);
  box-sizing: border-box;
  transition: border-color var(--ui-duration-fast) ease,
    background-color var(--ui-duration-fast) ease,
    box-shadow var(--ui-duration-fast) ease;
}

.ui-number-field.is-size-small {
  --ui-number-height: var(--ui-control-height-compact);
  --ui-number-step-width: 32px;
}

.ui-number-field.is-size-mini {
  --ui-number-height: 28px;
  --ui-number-step-width: 28px;
}

@media (hover: hover) and (pointer: fine) {
  .ui-number-field:hover:not(.is-disabled) {
    border-color: var(--ui-control-hover-border);
    background: linear-gradient(var(--ui-control-hover-background), var(--ui-control-hover-background)), var(--ui-bg-control);
  }
}

.ui-number-field:focus-within:not(.is-disabled) {
  border-color: rgba(var(--ui-color-primary-rgb), .78);
  background: var(--ui-bg-control-elevated);
  box-shadow: 0 0 0 3px rgba(var(--ui-color-primary-rgb), .1);
}

.ui-number-field.is-block {
  width: 100%;
}

.ui-number-field > .ui-number-input {
  width: var(--ui-number-input-width);
  min-width: 0;
  height: 100%;
  min-height: 0 !important;
  flex: 1 1 var(--ui-number-input-width);
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
}

.ui-number-field .el-input,
.ui-number-field .el-input__inner {
  height: 100% !important;
  min-height: 0 !important;
}

.ui-number-field .el-input__inner {
  padding-right: calc(var(--ui-number-step-width) + 8px);
  padding-left: calc(var(--ui-number-step-width) + 8px);
  color: var(--ui-text-primary);
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  font-size: var(--ui-font-size-form-control);
  line-height: var(--ui-number-height);
}

.ui-number-field .el-input-number.is-without-controls .el-input__inner {
  padding-right: 10px;
  padding-left: 10px;
}

.ui-number-field .el-input-number__increase,
.ui-number-field .el-input-number__decrease {
  width: var(--ui-number-step-width);
  height: 100%;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ui-text-secondary) !important;
  border: 0 !important;
  background: rgba(var(--ui-bg-elevated-rgb), .56) !important;
  box-sizing: border-box;
  line-height: 1;
}

.ui-number-field .el-input-number__increase {
  right: 0;
  border-left: 1px solid var(--ui-border-color) !important;
}

.ui-number-field .el-input-number__decrease {
  left: 0;
  border-right: 1px solid var(--ui-border-color) !important;
}

@media (hover: hover) and (pointer: fine) {
  .ui-number-field .el-input-number__increase:hover,
  .ui-number-field .el-input-number__decrease:hover {
    color: var(--ui-color-primary) !important;
    background: rgba(var(--ui-color-primary-rgb), .08) !important;
  }
}

.ui-number-field__addon {
  min-width: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  padding: 0 var(--ui-space-md);
  color: var(--ui-text-secondary);
  background: rgba(var(--ui-bg-elevated-rgb), .56);
  box-sizing: border-box;
  white-space: nowrap;
}

.ui-number-field__prefix {
  width: 60px;
  border-right: 1px solid var(--ui-border-color);
}

.ui-number-field__unit {
  width: 56px;
  border-left: 1px solid var(--ui-border-color);
}

.ui-number-field.is-disabled {
  border-color: var(--ui-border-color-subtle);
  background: rgba(var(--ui-bg-control-rgb), .68);
}

.ui-number-field.is-disabled .ui-number-field__addon {
  color: var(--ui-text-disabled);
}
</style>
